import {
  call,
  take,
  takeEvery,
  takeLatest,
  select,
  put,
  debounce,
  all,
  race,
} from 'redux-saga/effects';

import {
  getLastValues,
  uploadVariablesExcel as uploadVarsExcelApi,
  downloadVariablesExcelTemplate as downloadVarsExcelApi,
  getSeries,
} from '../../services';
import { downloadBlob } from '../../utils';
import { requestVisualizations } from '../visualizations';
import { displayNotification, checkOnline, getNotification } from '../notifications';
import { REFRESH_VALUES, setPollingActive, setPollingActiveDone } from '../application';
import { requestSiteVariables, getSourceConnectivityTree } from '../sources';
import {
  RECEIVE_LAST_SUBSCRIPTIONS,
  ADD_LAST_SUBSCRIPTIONS,
  FETCH_LAST_VARIABLE_DATA,
  SET_VARIABLES_LAST_POLLING,
  DOWNLOAD_VARIABLES_TEMPLATE,
  UPLOAD_VARIABLES_EXCEL_SHEET,
  FETCH_EXTENDED_SERIES_DATA,
  STOP_FETCH_EXTENDED_SERIES_DATA,
  receiveLastValues,
  setVariablesImportedAt,
  setImportErrors,
} from './actions';

export function* doFetchLastVariableData() {
  const subscriptions = yield select((state) => state.variables.lastSubscriptions);
  const variables = Object.keys(subscriptions).filter(
    (variableId) => Object.keys(subscriptions[variableId]).length
  );

  if (!variables.length) return;

  try {
    const payload = {
      variables: variables.map((variableId) => ({ id: variableId })),
    };
    yield put({
      type: SET_VARIABLES_LAST_POLLING,
      data: variables.reduce((acc, variable) => {
        acc[variable] = true;
        return acc;
      }, {}),
    });

    const { values } = yield call(getLastValues, payload);
    yield put(receiveLastValues(values));
  } catch (e) {
    console.error('Unable to fetch last variable data: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getLastVarValues', 'error')(e.message)));
  }
  yield put({
    type: SET_VARIABLES_LAST_POLLING,
    data: variables.reduce((acc, variable) => {
      acc[variable] = false;
      return acc;
    }, {}),
  });
}

export function* doGetExtendedSeriesData(action) {
  // like get series for visualizations, but will use req_type to bypass interval checks in data-service
  // and return up to 10k data points for signal viewer or export
  const { startDate, endDate, variables, interval, aggregation, callback } = action;

  if (!variables?.length) return;

  try {
    const connection = yield select(getSourceConnectivityTree);
    const payload = {
      from: startDate,
      to: endDate,
      interval,
      req_type: 'export_variable', // TODO: rename this to reflect the greater allowance on interval
      variables: variables.map((variable) => ({
        id: variable.id,
        aggregate: aggregation || variable.aggregate,
      })),
      connection,
    };
    const { values } = yield call(getSeries, payload);
    // Consider data points which has good quality
    const filteredValues = values.map((v) => ({
      ...v,
      values: v.values.map(([time, value, quality]) => [
        time,
        quality !== 1 ? null : value,
        quality,
      ]),
    }));
    if (callback) {
      yield call(callback, filteredValues);
    }
  } catch (e) {
    console.error('Unable to export the variable data: ', e);
    yield call(checkOnline);
    if (callback) {
      yield call(callback, { hasError: true });
    }
    yield put(displayNotification(getNotification('getExtendedSeries', 'error')(e.message)));
  }
}

export function* doStartGetExtendedSeriesData(action) {
  yield race([call(doGetExtendedSeriesData, action), take(STOP_FETCH_EXTENDED_SERIES_DATA)]);
}

export function* doFetchAllVariableData() {
  yield put(setPollingActive('variables'));
  yield all([doFetchLastVariableData()]);
  yield put(setPollingActiveDone('variables'));
}

export function* doAddLastSubscriptions(action) {
  const { variableIds, panelId } = action;
  const subscriptions = yield select((state) => state.variables.lastSubscriptions);
  let shouldTriggerImmediateFetch = false;

  variableIds.forEach((variableId) => {
    if (
      !Object.keys(subscriptions).includes(variableId) ||
      !Object.keys(subscriptions[variableId]).length
    ) {
      shouldTriggerImmediateFetch = true;
    }
  });

  yield put({
    type: RECEIVE_LAST_SUBSCRIPTIONS,
    panelId,
    variableIds,
  });

  if (shouldTriggerImmediateFetch) {
    // New subscriptions, trigger data fetching.
    yield put({ type: FETCH_LAST_VARIABLE_DATA });
  }
}

export function* doUploadVarsExcel(action) {
  const { file, siteId, org, template } = action;
  try {
    yield put(setVariablesImportedAt(0));
    const fd = new FormData();
    fd.append('file', file);
    fd.append('site', siteId);
    fd.append('org', org);
    fd.append('template', template);
    const response = yield call(uploadVarsExcelApi, fd);
    if (response.importErrors.length) {
      yield put(setImportErrors(response.importErrors));
    } else {
      yield put(requestSiteVariables());
      yield put(requestVisualizations(siteId));
      yield put(setVariablesImportedAt(Date.now()));
      yield put(displayNotification(getNotification('uploadVarsExcel', 'success')()));
    }
  } catch (e) {
    console.error('Unable to upload variables excel: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('uploadVarsExcel', 'error')()));
  }
}

export function* doDownloadVarsTemplate(action) {
  const { site } = action;
  try {
    const file = yield call(downloadVarsExcelApi, {
      site: site.id,
      org: site.org,
      template: site.template,
    });
    downloadBlob(
      file.data,
      `${site.name.replace(/\s/g, '_')}-signals_template.xlsx`,
      file.headers['content-type']
    );
  } catch (e) {
    console.error('Unable to download variables excel: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('downloadVarsExcel', 'error')()));
  }
}

export const sagas = [
  takeEvery(ADD_LAST_SUBSCRIPTIONS, doAddLastSubscriptions),
  takeLatest(DOWNLOAD_VARIABLES_TEMPLATE, doDownloadVarsTemplate),
  takeLatest(UPLOAD_VARIABLES_EXCEL_SHEET, doUploadVarsExcel),
  takeLatest(FETCH_EXTENDED_SERIES_DATA, doStartGetExtendedSeriesData),
  debounce(500, FETCH_LAST_VARIABLE_DATA, doFetchLastVariableData),
  debounce(500, REFRESH_VALUES, doFetchAllVariableData),
];
